import { useState, useEffect, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { PlusCircle, Edit, Trash2, Search } from 'lucide-react';
import RuleBuilderCanvas from '@/components/RuleBuilderCanvas';
import { useOfferRules, OfferRule } from '@/hooks/useOfferRules';
import { useOfferTemplates } from '@/hooks/useOfferTemplates';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { availableFields } from '@/utils/ruleDefinitions';
import { formatCondition, combinatorWord, combinatorJoiner } from '@/utils/ruleFormat';

const OfferDesignerPage = () => {
  const [isCreatingRule, setIsCreatingRule] = useState(false);
  const [editingRule, setEditingRule] = useState<OfferRule | null>(null);
  const { rules, loading, error, fetchOfferRules, deleteOfferRule, updateOfferRule, createOfferRule } = useOfferRules();
  const { offerTemplates, fetchOfferTemplates } = useOfferTemplates();

  // State for filters
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all'); // 'all', 'active', 'inactive'
  const [tagFilter, setTagFilter] = useState('all');

  useEffect(() => {
    fetchOfferRules();
    fetchOfferTemplates();
  }, [fetchOfferRules, fetchOfferTemplates]);

  const handleSave = async (rule: OfferRule) => {
    try {
      if (editingRule && editingRule.id) {
        await updateOfferRule(rule);
      } else {
        await createOfferRule(rule);
      }
      setIsCreatingRule(false);
      setEditingRule(null);
    } catch (error) {
      console.error('Failed to save rule:', error);
    }
  };

  const handleCancel = () => {
    setIsCreatingRule(false);
    setEditingRule(null);
  };

  const handleEdit = (rule: OfferRule) => {
    setEditingRule(rule);
    setIsCreatingRule(true);
  };

  const handleDelete = async (ruleId: string) => {
    await deleteOfferRule(ruleId);
  };

  const handleToggleActive = async (rule: OfferRule, newActiveState: boolean) => {
    if (rule.id) {
      await updateOfferRule({ ...rule, is_active: newActiveState });
    }
  };

  const allTags = useMemo(() => {
    const tags = new Set<string>();
    rules.forEach(rule => {
      (rule.tags ?? []).forEach(tag => tags.add(tag));
    });
    return ['all', ...Array.from(tags)];
  }, [rules]);

  const filteredRules = useMemo(() => {
    return rules.filter(rule => {
      const searchTermMatch = searchTerm === '' ||
        rule.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (rule.description ?? '').toLowerCase().includes(searchTerm.toLowerCase());

      const statusMatch = statusFilter === 'all' ||
        (statusFilter === 'active' && rule.is_active) ||
        (statusFilter === 'inactive' && !rule.is_active);

      const tagMatch = tagFilter === 'all' || (rule.tags ?? []).includes(tagFilter);

      return searchTermMatch && statusMatch && tagMatch;
    });
  }, [rules, searchTerm, statusFilter, tagFilter]);

  const offerName = (templateId?: string) =>
    offerTemplates.find((t) => t.offer_id === templateId)?.offer_name;

  return (
    <div className="pb-6">
      <div className="mb-6 sm:mb-8">
        <div className="flex flex-col gap-4 sm:flex-row sm:justify-between sm:items-center">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-foreground">Offer Designer</h1>
            <p className="text-muted-foreground mt-1 text-sm">
              Create and manage rules that dynamically assign offers to customers.
            </p>
          </div>
          {!isCreatingRule && (
            <Button
              onClick={() => setIsCreatingRule(true)}
              className="flex items-center justify-center gap-2 h-12 px-4 text-base font-medium bg-brand-red hover:bg-brand-red/90 text-brand-white self-start"
            >
              <PlusCircle className="h-5 w-5" />
              <span>Create new rule</span>
            </Button>
          )}
        </div>
      </div>

      {isCreatingRule ? (
        <RuleBuilderCanvas onSave={handleSave} onCancel={handleCancel} initialData={editingRule} offerTemplates={offerTemplates} />
      ) : (
        <div className="space-y-4">
          {/* Filter toolbar */}
          <Card>
            <CardContent className="p-4">
              <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
                <div className="relative w-full lg:max-w-sm">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                  <Input
                    placeholder="Search by name or description..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>
                <div className="flex flex-col gap-3 sm:flex-row lg:ml-auto">
                  <Select value={statusFilter} onValueChange={setStatusFilter}>
                    <SelectTrigger className="w-full sm:w-[170px]">
                      <SelectValue placeholder="Filter by status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All statuses</SelectItem>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="inactive">Inactive</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select value={tagFilter} onValueChange={setTagFilter}>
                    <SelectTrigger className="w-full sm:w-[170px]">
                      <SelectValue placeholder="Filter by tag" />
                    </SelectTrigger>
                    <SelectContent>
                      {allTags.map(tag => (
                        <SelectItem key={tag} value={tag}>{tag === 'all' ? 'All tags' : tag}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>

          {loading && <div className="rounded-lg border bg-card py-12 text-center text-muted-foreground">Loading rules...</div>}
          {error && (
            <div className="rounded-lg border border-status-danger/30 bg-status-danger/10 py-12 text-center text-status-danger">
              Error: {error}
            </div>
          )}
          {!loading && !error && filteredRules.length === 0 && (
            <div className="rounded-lg border bg-card py-12 text-center">
              <p className="text-lg text-muted-foreground">No rules match your filters.</p>
              <p className="mt-2 text-sm text-muted-foreground">Try adjusting your search or filter criteria.</p>
            </div>
          )}

          {!loading && !error && filteredRules.map((rule) => (
            <Card key={rule.id} className="p-4 sm:p-5">
              {/* Top row: identity + controls. Controls drop below the name on phones. */}
              <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <div className="min-w-0">
                  <h3 className="text-base font-semibold text-foreground">{rule.name}</h3>
                  {rule.description && (
                    <p className="text-sm text-muted-foreground">{rule.description}</p>
                  )}
                  <div className="mt-2 flex flex-wrap gap-2">
                    <Badge variant={rule.is_active ? 'default' : 'secondary'}>
                      {rule.is_active ? 'Active' : 'Inactive'}
                    </Badge>
                    <Badge variant="outline">Priority {rule.priority}</Badge>
                    {(rule.tags ?? []).map((tag) => (
                      <Badge key={tag} variant="outline">{tag}</Badge>
                    ))}
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <div className="mr-1 flex items-center gap-2">
                    <Switch
                      id={`active-toggle-${rule.id}`}
                      checked={rule.is_active}
                      onCheckedChange={(checked) => handleToggleActive(rule, checked)}
                    />
                    <Label htmlFor={`active-toggle-${rule.id}`} className="text-xs text-muted-foreground">
                      Active
                    </Label>
                  </div>
                  <Button variant="ghost" size="icon" aria-label={`Edit ${rule.name}`} onClick={() => handleEdit(rule)}>
                    <Edit className="h-4 w-4" />
                  </Button>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        aria-label={`Delete ${rule.name}`}
                        className="text-muted-foreground hover:text-status-danger"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Delete “{rule.name}”?</AlertDialogTitle>
                        <AlertDialogDescription>
                          This deletes the rule along with its condition groups, conditions, and
                          actions. It cannot be undone. Offers already assigned to customers are not
                          affected.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Keep it</AlertDialogCancel>
                        <AlertDialogAction
                          onClick={() => handleDelete(rule.id!)}
                          className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                        >
                          Delete rule
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </div>

              {/* Summary: conditions + actions, plain language, stacks on phones. */}
              <div className="mt-4 grid gap-4 sm:grid-cols-2">
                <div className="rounded-lg border bg-muted/40 p-3">
                  <p className="mb-1.5 text-xs font-bold uppercase tracking-wide text-brand-gray">Conditions</p>
                  {(rule.condition_groups ?? []).length > 0 ? (
                    <div className="space-y-2">
                      {(rule.condition_groups ?? []).map((group, groupIndex) => (
                        <div key={group.id || groupIndex}>
                          {groupIndex > 0 && (
                            <p className="mb-2 text-xs font-bold uppercase tracking-wide text-muted-foreground">
                              {combinatorJoiner(rule.group_combinator)}
                            </p>
                          )}
                          <p className="text-xs text-muted-foreground">
                            Group {groupIndex + 1}: match {combinatorWord(group.combinator)}
                          </p>
                          <ul className="mt-1 space-y-0.5">
                            {(group.conditions ?? []).map((condition, condIndex) => (
                              <li key={condition.id || condIndex} className="flex items-start gap-1.5 text-sm text-foreground">
                                <span className="mt-2 h-1 w-1 flex-none rounded-full bg-muted-foreground" />
                                <span>{formatCondition(condition, availableFields)}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">No conditions defined.</p>
                  )}
                </div>
                <div className="rounded-lg border bg-muted/40 p-3">
                  <p className="mb-1.5 text-xs font-bold uppercase tracking-wide text-brand-gray">Actions</p>
                  {(rule.actions ?? []).length > 0 ? (
                    <ul className="space-y-0.5">
                      {(rule.actions ?? []).map((action, actionIndex) => (
                        <li key={action.id || actionIndex} className="flex items-start gap-1.5 text-sm text-foreground">
                          <span className="mt-2 h-1 w-1 flex-none rounded-full bg-status-success" />
                          <span>
                            Assign offer: {offerName(action.value?.offer_template_id) ?? 'not set'}
                          </span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-sm text-muted-foreground">No actions defined.</p>
                  )}
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default OfferDesignerPage;
